import { ChevronRight, Menu, Search } from 'lucide-react'
import { Link, useLocation } from 'react-router-dom'
import { useUIStore } from '@/stores/useUIStore'
import { getPageBreadcrumb } from '@/config/pageBreadcrumb'
import { AttendanceHeaderPill } from './AttendanceHeaderPill'
import { NotificationDropdown } from './NotificationDropdown'
import { UserMenu } from './UserMenu'
import { ThemeToggleButton } from './ThemeToggleButton'
import { getSearchShortcutLabel } from '@/lib/keyboard'

export interface HeaderProps {
  /** Open the command palette (also bound to Cmd/Ctrl+K). */
  onOpenSearch: () => void
}

/**
 * Top header with hamburger (mobile), page breadcrumb, notifications, and user menu.
 *
 * The breadcrumb ("Section › Page") lives here in the header bar — using the otherwise-empty
 * left space — rather than as a separate row inside each page, so it's consistent across every
 * route and costs no extra vertical space. Labels come from {@link getPageBreadcrumb}.
 */
export function Header({ onOpenSearch }: HeaderProps): React.ReactElement {
  const toggleSidebar = useUIStore((s) => s.toggleSidebar)
  const { pathname } = useLocation()
  const trail = getPageBreadcrumb(pathname)
  const searchShortcutLabel = getSearchShortcutLabel()

  return (
    <header className="sticky top-0 z-30 grid h-16 grid-cols-[minmax(0,1fr)_auto] items-center gap-4 border-b border-gray-200/80 bg-white/95 px-4 backdrop-blur dark:border-gray-800 dark:bg-gray-900/95 sm:grid-cols-[minmax(0,1fr)_minmax(19rem,34rem)_minmax(0,1fr)] sm:gap-5 md:px-6 xl:grid-cols-[minmax(0,1fr)_minmax(32rem,44rem)_minmax(0,1fr)]">
      <div className="flex min-w-0 items-center sm:mr-1 sm:border-r sm:border-gray-200/80 sm:pr-5 dark:sm:border-gray-700/80">
        <button
          type="button"
          onClick={toggleSidebar}
          className="rounded-xl p-2 text-gray-400 transition-colors hover:bg-gray-100/80 hover:text-gray-600 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary/40 md:hidden dark:text-gray-500 dark:hover:bg-white/5 dark:hover:text-gray-300"
          aria-label="Open menu"
        >
          <Menu className="h-5 w-5" aria-hidden />
        </button>
        {trail ? (
          <nav
            className="ml-1 hidden min-w-0 items-center gap-2 text-sm sm:flex md:ml-0"
            aria-label="Breadcrumb"
          >
            {trail.map((crumb, index) => {
              const isLast = index === trail.length - 1
              return (
                <div key={index} className="flex min-w-0 items-center gap-2">
                  {index > 0 && (
                    <ChevronRight
                      className="h-4 w-4 shrink-0 text-gray-300 dark:text-gray-600"
                      aria-hidden
                    />
                  )}
                  {isLast || !crumb.to ? (
                    <span className="truncate font-semibold text-gray-800 dark:text-gray-100">
                      {crumb.label}
                    </span>
                  ) : (
                    <Link
                      to={crumb.to}
                      className="shrink-0 text-gray-500 transition-colors hover:text-gray-900 dark:text-gray-400 dark:hover:text-gray-100"
                    >
                      {crumb.label}
                    </Link>
                  )}
                </div>
              )
            })}
          </nav>
        ) : null}
      </div>

      <div className="hidden w-full min-w-0 justify-self-center sm:block">
        <button
          type="button"
          onClick={onOpenSearch}
          className="group flex h-11 w-full items-center justify-between rounded-xl border border-primary/20 bg-white px-3 text-sm font-semibold text-gray-700 ring-1 ring-black/5 transition-colors hover:border-primary/35 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary/35 dark:border-primary/35 dark:bg-gray-800 dark:text-gray-100 dark:ring-white/10 dark:hover:border-primary/55"
          aria-label="Search"
          title={`Search (${searchShortcutLabel})`}
        >
          <span className="flex min-w-0 items-center gap-3">
            <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-lg bg-primary text-white shadow-sm transition-colors group-hover:bg-primary/90">
              <Search className="h-4 w-4" aria-hidden />
            </span>
            <span className="truncate">Search workspace</span>
          </span>
          <kbd className="ml-3 shrink-0 rounded-md border border-gray-200 bg-gray-50 px-2 py-1 font-sans text-[11px] font-bold text-gray-500 shadow-sm dark:border-gray-600 dark:bg-gray-700/70 dark:text-gray-300">
            {searchShortcutLabel}
          </kbd>
        </button>
      </div>

      <div className="flex min-w-0 items-center justify-end gap-1 justify-self-end">
        <AttendanceHeaderPill />
        <ThemeToggleButton />
        <NotificationDropdown />
        <UserMenu />
      </div>
    </header>
  )
}
